/**
 * Shared Square Setup Guide Component
 * Used in both Pickup Safety and Parent SMS Communications
 * 3-step setup for Option C (app handles billing automatically)
 */
import { useState } from "react";
import { CheckCircle2 } from "lucide-react";

const WEBHOOK_URL = "https://login.todayskidzsimplesignin.com/api/square/webhook";

const SquareSetupGuide = ({ squareConnected, onConnect }) => {
  const [expanded, setExpanded] = useState(!squareConnected);

  const steps = [
    {
      num: 1, done: squareConnected,
      title: "Connect Your Square Account",
      detail: "Link your Square account to this app. The app will automatically generate payment links and SMS them to parents on your billing date — no catalog items or subscription plans needed.",
      action: !squareConnected ? { label: "Connect Square Account →", onClick: onConnect } : null
    },
    {
      num: 2, done: true,
      title: "Add Webhook in Square Developer Console",
      detail: (
        <span>Go to <strong>Square Developer Console → Webhooks</strong> → add this URL: <strong className="text-blue-700 break-all">{WEBHOOK_URL}</strong> — subscribe to the <em>payment.completed</em> event. This tells the app when a parent pays.</span>
      )
    },
    {
      num: 3, done: true,
      title: "You're done — the app handles the rest",
      detail: "Set your fee per child and billing date. On billing day, the app automatically calculates each parent's charge, generates a Square payment link, and SMS it to them. When they pay, you'll see it in the payment tracker."
    },
  ];

  if (!expanded && squareConnected) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">Square setup complete — account connected</span>
        </div>
        <button onClick={() => setExpanded(true)} className="text-xs text-gray-400 hover:text-gray-600 underline ml-3 flex-shrink-0">View guide</button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-blue-800">Square Setup Guide</p>
        {squareConnected && (
          <button onClick={() => setExpanded(false)} className="text-xs text-gray-500 hover:text-gray-700 underline">Hide — setup complete</button>
        )}
      </div>
      <div className="space-y-3">
        {steps.map(step => (
          <div key={step.num} className={`flex gap-3 p-3 rounded-lg border ${step.done ? 'bg-white border-gray-200' : 'bg-rose-50 border-rose-200'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 ${step.done ? 'bg-emerald-500 text-white' : 'bg-rose-400 text-white'}`}>
              {step.done ? '✓' : step.num}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">{step.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{step.detail}</p>
              {step.action && (
                <button onClick={step.action.onClick}
                  className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium">
                  {step.action.label}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400">Fees: 2.9% + $0.30 (Square) + Our $5 platform fee can be passed to parent</p>
    </div>
  );
};

export default SquareSetupGuide;
